/* eslint-disable import/no-cycle */
import {FilterModel, FilterType, IFilterModel, IQueryParamsFilter, ISorting} from '../types/galleryTypes';
import {PriceFilterModel} from '../models/PriceFilterModel';
import {QueryParamsService} from '@wix/wixstores-client-storefront-sdk/dist/src/services/QueryParamsService';

export enum DefaultQueryParamKeys {
  Sort = 'sort',
  Page = 'page',
}

export class GalleryQueryParamsService extends QueryParamsService {
  public readonly getFiltersFromQueryParams = (
    filterModels: FilterModel[],
    isSplitGalleryFiltersWithCommaEnabled: boolean
  ): IQueryParamsFilter[] => {
    const decodedSearchParams = this.getSearchParamsDecoded(this.location.query);
    const activeFilters = [];

    const delimiter = isSplitGalleryFiltersWithCommaEnabled ? ',' : '|';

    filterModels.forEach((filter) => {
      if (decodedSearchParams[filter.title]) {
        const paramsFilter = decodedSearchParams[filter.title];
        switch (filter.filterType) {
          case FilterType.LIST_OPTION:
          case FilterType.CUSTOM_COLLECTION:
          case FilterType.COLOR_OPTION:
          case FilterType.COLLECTION: {
            activeFilters.push({
              key: filter.title,
              value: paramsFilter,
              filterId: filter.filterId,
            });

            break;
          }
          case FilterType.PRICE: {
            const priceValue = paramsFilter.split('-');
            activeFilters.push({
              key: filter.title,
              value: `${priceValue[0]}${delimiter}${priceValue[1]}`,
              filterId: filter.filterId,
            });
          }
        }
        this.setAndCurrentUpdateQueryParams(filter.title, paramsFilter);
      }
    });

    return activeFilters.length > 0 ? activeFilters : null;
  };

  public readonly updatePageQueryParam = (page?: number): void => {
    if (page) {
      this.setAndUpdateQueryParams(DefaultQueryParamKeys.Page, page.toString());
    } else {
      this.removeQueryParam(DefaultQueryParamKeys.Page);
    }
  };

  public readonly updateQueryParamsBySort = (selectedSort: ISorting) => {
    if (selectedSort.id === 'default') {
      this.removeQueryParam(DefaultQueryParamKeys.Sort);
    } else {
      this.setAndUpdateQueryParams(DefaultQueryParamKeys.Sort, selectedSort.id);
    }
  };

  public readonly clearAllQueryParamsFilters = (paramTitles: string[]) => {
    this.location.queryParams.remove(paramTitles);
    paramTitles.forEach((title) => {
      const index = this.currentUrlSearchParams.findIndex((param) => param.paramTitle === title);
      if (index !== -1) {
        this.currentUrlSearchParams.splice(index, 1);
      }
    });
  };

  public readonly updateQueryParamsByFilters = (
    filterId: number,
    filterModels: FilterModel[],
    mainCollectionId: string,
    isSplitGalleryFiltersWithCommaEnabled: boolean
  ) => {
    const filterModel = filterModels.find((fm) => fm.filterId === filterId) as IFilterModel;
    let activeFilterOption: string = '';

    const delimiter = isSplitGalleryFiltersWithCommaEnabled ? ',' : '|';

    switch (filterModels[filterId].filterType) {
      case FilterType.COLLECTION: {
        if (filterModel.activeOptions === mainCollectionId) {
          activeFilterOption = `All`;
        } else {
          const filterOption = filterModel.options.find((option) => option.key === filterModel.activeOptions);
          activeFilterOption = filterOption.value;
        }
        break;
      }
      case FilterType.LIST_OPTION:
      case FilterType.CUSTOM_COLLECTION:
      case FilterType.COLOR_OPTION: {
        const filterOptions = filterModel.options.filter(
          (option) => filterModel.activeOptions.indexOf(option.key) > -1
        );

        if (filterOptions.length === 1) {
          activeFilterOption = filterOptions[0].value;
        } else if (filterOptions.length === 0) {
          this.removeQueryParam(filterModel.title);
        } else {
          activeFilterOption = filterOptions.map((filter) => filter.value).join(delimiter);
        }

        break;
      }
      case FilterType.PRICE: {
        activeFilterOption = `${(filterModel.activeOptions as PriceFilterModel).minPrice}-${
          (filterModel.activeOptions as PriceFilterModel).maxPrice
        }`;
      }
    }
    if (activeFilterOption) {
      this.setAndUpdateQueryParams(filterModel.title, activeFilterOption);
    }
  };

  private readonly getSearchParamsDecoded = (searchParams: {
    [paramKey: string]: string;
  }): {[paramKey: string]: string} => {
    return Object.keys(searchParams).reduce((res, key) => {
      const decodedKey = this.safeDecode(key);
      const decodedValue = this.safeDecode(searchParams[key]);
      const isParamValid = decodedKey && decodedValue;

      return isParamValid
        ? {
            ...res,
            [decodedKey]: decodedValue,
          }
        : res;
    }, {});
  };

  private readonly removeQueryParam = (paramTitle) => {
    const index = this.currentUrlSearchParams.findIndex((param) => param.paramTitle === paramTitle);

    this.currentUrlSearchParams.splice(index, 1);
    this.location.queryParams.remove([paramTitle]);
  };

  private readonly setAndUpdateQueryParams = (paramTitle: string, paramValue: string) => {
    this.setAndCurrentUpdateQueryParams(paramTitle, paramValue);

    this.location.queryParams.add({[paramTitle]: paramValue});
  };

  private readonly safeDecode = (str: string) => {
    let decoded;
    try {
      decoded = decodeURIComponent(str).replace(/\+/g, ' ');
    } catch (e) {
      //All of our filter query params can be decoded, this only happens when users add custom query params and there's no need to alert it.
    }
    return decoded;
  };
}
